// AGC: NoteDetailView — Full-screen modal showing note details
// AGC: Media preview, text content, GPS link to Google Maps, author, tagged users

import React from 'react'
import { X, MapPin, Camera, Video, Mic, FileText, User, Clock } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { openInGoogleMaps, formatCoordinates } from '../../utils/gpsUtils'
import type { LotNote } from '../../types/lotNotes'

interface NoteDetailViewProps {
  note: LotNote | null
  isOpen: boolean
  onClose: () => void
}

const NOTE_TYPE_CONFIG = {
  photo: { icon: Camera, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Foto' },
  video: { icon: Video, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Vídeo' },
  audio: { icon: Mic, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Áudio' },
  text: { icon: FileText, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Nota' },
}

export const NoteDetailView: React.FC<NoteDetailViewProps> = ({ note, isOpen, onClose }) => {
  const { t } = useTranslation()

  if (!isOpen || !note) return null

  const config = NOTE_TYPE_CONFIG[note.type]
  const TypeIcon = config.icon
  const createdDate = new Date(note.createdAt)

  return (
    <div className="fixed inset-0 z-[99999] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.bg}`}>
              <TypeIcon className={`w-5 h-5 ${config.color}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('notes.noteDetail')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{config.label}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* AGC: Content */}
        <div className="flex-1 overflow-y-auto">
          {/* AGC: Media preview */}
          {note.mediaUrl && (
            <div className="bg-black">
              {note.type === 'photo' || (note.type === 'text' && note.mediaUrl?.match(/\.(jpg|jpeg|png|gif|webp)/i)) ? (
                <img src={note.mediaUrl} alt="" className="w-full max-h-[40vh] object-contain" />
              ) : note.type === 'video' || (note.type === 'text' && note.mediaUrl?.match(/\.(mp4|webm|mov)/i)) ? (
                <video src={note.mediaUrl} controls playsInline className="w-full max-h-[40vh]" />
              ) : note.type === 'audio' || (note.type === 'text' && note.mediaUrl?.match(/\.(webm|mp3|wav|m4a|ogg)/i)) ? (
                <div className="p-6 flex items-center justify-center">
                  <audio src={note.mediaUrl} controls className="w-full max-w-sm" />
                </div>
              ) : null}
            </div>
          )}

          {/* AGC: Text content */}
          <div className="p-4 space-y-4">
            {note.content && (
              <p className="text-gray-900 dark:text-white text-sm whitespace-pre-wrap">
                {note.content}
              </p>
            )}

            {/* AGC: Metadata rows */}
            <div className="space-y-2">
              {/* AGC: Author */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <User className="w-4 h-4 shrink-0" />
                <span>{note.userName || note.createdByName || t('notes.recordedBy')}</span>
              </div>

              {/* AGC: Timestamp */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4 shrink-0" />
                <span>
                  {createdDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  {' '}
                  {createdDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              {/* AGC: GPS coordinates — clickable → Google Maps */}
              {note.gpsLocation && (
                <button
                  onClick={() => openInGoogleMaps(note.gpsLocation!.latitude, note.gpsLocation!.longitude)}
                  className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline"
                >
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span>{formatCoordinates(note.gpsLocation.latitude, note.gpsLocation.longitude)}</span>
                  <span className="text-xs text-gray-400">({t('notes.openInMaps')})</span>
                </button>
              )}

              {/* AGC: Duration for audio/video — guard against Infinity/NaN */}
              {note.mediaDuration != null && isFinite(note.mediaDuration) && note.mediaDuration > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="text-xs">
                    {Math.floor(note.mediaDuration / 60)}:{Math.floor(note.mediaDuration % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AGC: Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NoteDetailView
